import React, { useCallback, useEffect, useState } from 'react'
import { Tabs } from '../../../../../components/Tabs/Tabs'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { MeetingResults } from './MeetingResults'
import { Col, Container, Placeholder, Row, Tab } from 'react-bootstrap'
import { selectMeetingSummaryLoading } from '../../../../../store/meetingSummary/meetingSummary'
import { MeetingSummaryAgent } from '../MeetingSummaryAgent/MeetingSummaryAgent'
import { selectMeetingSummaryAgentEditing, setMeetingSummaryEditing } from '../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { selectEditingAiResultIds, stopEditingAiResults } from '../../../../../store/meetingSummary/display'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { TrackEvent, useTracking } from '../../common/tracking'
import { useCookies } from 'react-cookie'
import { Modal } from '../../../../../components/Modal/Modal'
import { Button } from '../../../../../components/Button/Button'
import { useBeforeUnload } from 'react-router-dom'
import { Forms } from '../FormFills/Forms'
import PrizeBadge from '../../../../../components/ScopedBlock/PrizeBadge.svg?url'
import './MeetingResults.scss'
import { MeetingScorecard } from '../MeetingScorecard/MeetingScorecard'
import { ClientProfileEventPoller } from '../ClientProfile/Poller/ClientProfileEventPoller'
import { ClientProfilePoller } from '../ClientProfile/Poller/ClientProfilePoller'
import { ClientProfileInitialization } from '../ClientProfile/Initialization/ClientProfileInitialization'
import { MeetingClientProfile } from '../ClientProfile/ClientProfile'
import { setFormFillStartedFrom } from '../../../../../store/meetingSummary/forms'

const TAB_COOKIE_MAX_AGE_THREE_MONTH = 60 * 60 * 24 * 30 * 3
export const MeetingResultsWrapper: React.FC = () => {
  const { hasQuota } = useZocksQuota()

  const isLoading = useAppSelector(selectMeetingSummaryLoading)
  const tabViewEnabled = hasQuota('ai.meetingSummaryAgent.value')

  if (isLoading) {
    return <MeetingResultsPlaceholder />
  }

  return tabViewEnabled ? <MeetingResultsTabView /> : <MeetingResults />
}

const MeetingResultsPlaceholder: React.FC = () => (
  <Container className='zocks-mt-3.5 mx-0 w-100 mw-100 p-0'>
    <Row className='p-0 gy-3 gx-3'>
      <Placeholder className='meeting-results-placeholder' as={Col} xs={12} lg={6} animation={'wave'}>
        <Placeholder bg={'light'} />
      </Placeholder>
      <Placeholder className='meeting-results-placeholder' as={Col} xs={12} lg={6} animation={'wave'}>
        <Placeholder bg={'light'} />
      </Placeholder>
      <Placeholder className='meeting-results-placeholder' as={Col} xs={12} lg={4} animation={'wave'}>
        <Placeholder bg={'light'} />
      </Placeholder>
      <Placeholder className='meeting-results-placeholder' as={Col} xs={12} lg={4} animation={'wave'}>
        <Placeholder bg={'light'} />
      </Placeholder>
      <Placeholder className='meeting-results-placeholder' as={Col} xs={12} lg={4} animation={'wave'}>
        <Placeholder bg={'light'} />
      </Placeholder>
    </Row>
  </Container>
)

const MeetingResultsTabView: React.FC = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()
  const [cookies, setCookie] = useCookies(['defaultMeetingSummaryTab'])
  const { hasQuota } = useZocksQuota()

  const meetingSummaryAgentEditing = useAppSelector(selectMeetingSummaryAgentEditing)
  const editingAiResults = useAppSelector(selectEditingAiResultIds)

  const [activeTabKey, setActiveTabKey] = useState<string>(cookies.defaultMeetingSummaryTab ?? 'meetingSummary')
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [tabToChange, setTabToChange] = useState<string | null>(null)

  const editing = (activeTabKey === 'meetingSummary' && meetingSummaryAgentEditing) || (activeTabKey === 'allMeetingDetails' && !!editingAiResults.length)
  const clientProfileAvailable = hasQuota('ai.clientProfile.value')
  const formEnabled = hasQuota('ai.formFill.value')

  useEffect(() => {
    if (activeTabKey === 'forms') {
      dispatch(setFormFillStartedFrom('meeting'))
    } else {
      dispatch(setFormFillStartedFrom(null))
    }
  }, [activeTabKey, dispatch])

  const formTitle = (
    <div className='d-flex align-items-center'>
      {!formEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Forms
    </div>
  )

  const clientProfileTitle = (
    <div className='d-flex align-items-center'>
      {!clientProfileAvailable ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Client Profile
    </div>
  )

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (editing) {
          e.preventDefault()
          e.returnValue = ''
        }
      },
      [editing],
    ),
  )

  const changeTabWithConfirmation = (key: string | null) => {
    if (!key || key === activeTabKey) {
      return
    }

    if (editing) {
      setTabToChange(key)
      setShowConfirmationModal(true)
    } else {
      changeTab(key)
    }
  }

  const _changeTab = (key: string | null) => {
    if (key) {
      dispatch(setMeetingSummaryEditing(false))
      dispatch(stopEditingAiResults())
      setActiveTabKey(key)
      setTabToChange(null)
      setCookie('defaultMeetingSummaryTab', key, { path: '/', maxAge: TAB_COOKIE_MAX_AGE_THREE_MONTH })
    }
  }

  const changeTab = tracked(
    _changeTab,
    { eventName: TrackEvent.MEETING_SUMMARY_TAB_CHANGED },
    {
      eventDetailsExtractor: (key) => (key ? { tab: key } : {}),
    },
  )

  const stay = () => {
    setTabToChange(null)
    setShowConfirmationModal(false)
  }

  const leave = () => {
    changeTab(tabToChange)
    setShowConfirmationModal(false)
  }

  return (
    <>
      {clientProfileAvailable && (
        <>
          <ClientProfileEventPoller />
          <ClientProfilePoller />
          <ClientProfileInitialization />
        </>
      )}

      <TabChangeConfirmationModal show={showConfirmationModal} onStay={stay} onLeave={leave} />
      <Tabs activeKey={activeTabKey} id='meeting-summary-tabs' className='d-flex meeting-results-tabs mb-4' onSelect={changeTabWithConfirmation}>
        <Tab eventKey='meetingSummary' title='Meeting Summary'>
          <MeetingSummaryAgent />
        </Tab>
        <Tab eventKey='allMeetingDetails' title='Meeting Details'>
          <MeetingResults />
        </Tab>
        <Tab eventKey='forms' title={formTitle} disabled={!formEnabled} mountOnEnter>
          <Forms />
        </Tab>
        {clientProfileAvailable && (
          <Tab eventKey='meetingClientProfile' title={clientProfileTitle} disabled={!clientProfileAvailable} mountOnEnter>
            <MeetingClientProfile />
          </Tab>
        )}
        <Tab eventKey='meetingScorecard' title='Scorecard' mountOnEnter>
          <MeetingScorecard />
        </Tab>
      </Tabs>
    </>
  )
}

interface TabChangeConfirmationModalProps {
  show: boolean
  onStay: () => void
  onLeave: () => void
}

const TabChangeConfirmationModal: React.FC<TabChangeConfirmationModalProps> = ({ show, onStay, onLeave }) => {
  return (
    <Modal
      show={show}
      onHide={onStay}
      header={<></>}
      body={
        <div className='d-flex flex-column zocks-gap-3'>
          <h4 className='text-center'>Are you sure you want to leave this page?</h4>
          <div className='paragraph-medium-extended'>It looks like you didn’t save the changes you made. Save before you go!</div>
        </div>
      }
      footer={
        <div className='d-flex flex-grow-1 gap-3 align-items-center'>
          <Button variant='secondary' className='flex-grow-1' onClick={onStay}>
            Stay
          </Button>
          <Button variant='danger' className='flex-grow-1' onClick={onLeave}>
            Leave
          </Button>
        </div>
      }
      centered={true}
    />
  )
}
